"use client"

import styled from "@emotion/styled"
import { Button, TextField, Typography } from "@mui/material"
import { GetHeadquartersJobsRequest, postCareerAllHeadquartersJobs } from "api"
import dynamic from "next/dynamic"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { Desktop, Mobile } from "ui"
import Container from "ui/Container"
import CloseXIcon from "ui/icons/CloseXIcon"
import Search from "ui/icons/Search"
import { sharedColors } from "ui/theme/colors"
import JobCategories from "./JobCategories"
import JobItem from "./JobItem"
import JobMobileCategory from "./JobMobileCategory"

const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const StyledJobsDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding-top: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const StyledButtonDiv = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
  }
`

const StyeldButton = styled(Button)`
  border: 1px solid #008761;
  height: 48px;
  border-radius: 12px;
  color: #008761;
  border: 1px solid #008761;
  font-size: 20px !important;
  ${({ theme }) => theme.breakpoints.down("md")} {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    min-width: 300px;
  }
`

const StyledTextField = styled(TextField)`
  width: 100%;
  margin-bottom: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 357px;
    margin-bottom: 48px;
  }

  svg[aria-label="close"] {
    cursor: pointer;
  }
  label {
    font-size: 16px;
    top: 0px;
    color: ${sharedColors.labelGray};
  }
  input {
    padding-top: 16px;
    padding-bottom: 17px;
  }
  label[data-shrink="true"] {
    transform: translate(10px, -35px);
  }
`

const StyledLoaderDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 493px;
  > div {
    width: 100px;
    height: 100px;
  }
`

const StyledNoResultDiv = styled.div`
  img {
    margin-top: 24px;
  }
  ${({ theme }) => theme.breakpoints.down("md")} {
    display: flex;
    flex-direction: column;
    text-align: left;
    align-items: center;
    p {
      width: 100%;
    }
  }
`
const StyledTitle = styled(Typography)<{ component?: React.ElementType }>`
  margin: 0px;
  font-size: 18px;
  font-weight: 400;
`
type Props = {
  jobResponse: any
}

const JobDisplay = ({ jobResponse }: Props) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [jobs, setJobs] = useState(jobResponse?.items || []) as any
  const categories = Array.from(
    new Set(jobResponse?.items?.map((job: any) => job.categoryName)),
  ) as any
  const [slicedIndex, setSlicedIndex] = useState(9)
  const [selectedCategories, setSelectedCategories] = useState(() => {
    // Initialize from URL params
    const categoriesParam = searchParams.get("categories")
    return categoriesParam ? categoriesParam.split(",") : []
  })
  const [mobileSelectedCategories, setMobileSelectedCategories] = useState(
    () => {
      // Initialize mobile categories from URL as well
      const categoriesParam = searchParams.get("categories")
      return categoriesParam ? categoriesParam.split(",") : []
    },
  )

  const [inputValue, setInputValue] = useState(() => {
    // Initialize search from URL params
    return searchParams.get("search") || ""
  })
  const [loading, setLoading] = useState(false)
  const [animationData, setAnimationData] = useState<any>(null)
  const firstNewJobRef = useRef<HTMLDivElement>(null)

  // Update URL when filters change
  const updateURL = (categories: string[], search: string) => {
    const params = new URLSearchParams()
    if (categories.length > 0) {
      params.set("categories", categories.join(","))
    }
    if (search) {
      params.set("search", search)
    }

    const newPath =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "")
    router.replace(newPath, { scroll: false })
  }

  useEffect(() => {
    // Using a simple fetch to get the animation JSON
    fetch("/Loading.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data))
      .catch((error) => console.error("Error loading animation:", error))
  }, [])

  // Whenever selectedCategories or search changes, fetch jobs and update URL
  useEffect(() => {
    const fetchFilteredJobs = async () => {
      try {
        setLoading(true)
        const requestData: GetHeadquartersJobsRequest = {
          searchTerm: inputValue,
          filters:
            selectedCategories?.length > 0
              ? [
                  {
                    filterType: "JobCategories",
                    values: selectedCategories,
                  },
                ]
              : [],
          requestPage: 0,
          pageSize: 10,
        }
        const response = await postCareerAllHeadquartersJobs(requestData)

        setJobs(response?.data?.body?.items)
        setSlicedIndex(9)
        setLoading(false)

        // Update URL with current filters
        updateURL(selectedCategories, inputValue)
      } catch (error) {
        setLoading(false)
        console.error("Error fetching filtered jobs:", error)
      }
    }

    fetchFilteredJobs()
  }, [selectedCategories, inputValue])

  // Handle focus after loading more jobs
  const focusTimerRef = useRef<NodeJS.Timeout | null>(null)
  const previousSliceIndex = useRef(slicedIndex)

  useEffect(() => {
    // Only focus if slice index increased (load more was clicked)
    if (slicedIndex > previousSliceIndex.current) {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current)
      }
      focusTimerRef.current = setTimeout(() => {
        if (firstNewJobRef.current) {
          firstNewJobRef.current.focus()
        }
        focusTimerRef.current = null
      }, 0)
    }
    previousSliceIndex.current = slicedIndex

    // Cleanup on unmount
    return () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current)
      }
    }
  }, [slicedIndex])

  const loadMoreJobs = () => {
    setSlicedIndex((prevIndex) => prevIndex + 9)
  }

  const handleCategoryClick = (category: string) => {
    // Toggle category selection
    if (selectedCategories.includes(category)) {
      setSelectedCategories(
        selectedCategories.filter((cat) => cat !== category),
      )
    } else {
      setSelectedCategories([...selectedCategories, category])
    }
    // Reset jobs slice index when filtering changes
    setSlicedIndex(9)
  }

  const handleAllCategories = () => {
    setSelectedCategories([])
    setSlicedIndex(9)
  }

  // Mobile: update temporary state only.
  const handleMobileCategoryClick = (category: string) => {
    if (mobileSelectedCategories.includes(category)) {
      setMobileSelectedCategories(
        mobileSelectedCategories.filter((cat) => cat !== category),
      )
    } else {
      setMobileSelectedCategories([...mobileSelectedCategories, category])
    }
  }

  const handleMobileAllCategories = () => {
    setMobileSelectedCategories([])
  }

  const handleMobileApplyFilters = () => {
    setSelectedCategories(mobileSelectedCategories)
    setSlicedIndex(9)
  }

  return (
    <Container paddingMobile>
      <StyledTextField
        name="search"
        label="חיפוש משרה"
        variant="filled"
        slotProps={{
          input: {
            endAdornment: inputValue ? (
              <CloseXIcon onClick={() => setInputValue("")} />
            ) : (
              <Search />
            ),
          },
        }}
        value={inputValue}
        onChange={(e: any) => setInputValue(e.target.value)}
      />
      {jobs && jobs.length > 0 && (
        <>
          <Mobile>
            <JobMobileCategory
              categories={categories}
              selectedCategories={mobileSelectedCategories}
              handleCategoryClick={handleMobileCategoryClick}
              handleAllCategories={handleMobileAllCategories}
              handleMobileApplyFilters={handleMobileApplyFilters}
            />
          </Mobile>
          <Desktop>
            <JobCategories
              categories={categories}
              selectedCategories={selectedCategories}
              handleCategoryClick={handleCategoryClick}
              handleAllCategories={handleAllCategories}
            />
          </Desktop>
        </>
      )}

      {!loading ? (
        <>
          {jobs && jobs.length > 0 ? (
            <>
              <StyledTitle component="h2">
                מצאנו <span style={{ fontWeight: 700 }}>{jobs?.length}</span>{" "}
                משרות פנויות
              </StyledTitle>
            </>
          ) : (
            <StyledNoResultDiv>
              <Typography variant="body2">
                חיפשנו ולא מצאנו משרות פנויות כרגע לבקשתך
              </Typography>
              <Desktop>
                <Image
                  src="/images/noresult.png"
                  alt="no result"
                  width={324}
                  height={324}
                />
              </Desktop>
              <Mobile>
                <Image
                  src="/images/noresult.png"
                  alt="no result"
                  width={120}
                  height={120}
                />
              </Mobile>
            </StyledNoResultDiv>
          )}
          <StyledJobsDiv role="list">
            {jobs &&
              jobs
                .slice(0, slicedIndex)
                .map((job: any, index: number) => (
                  <JobItem
                    job={job}
                    key={index}
                    ref={index === slicedIndex - 9 ? firstNewJobRef : undefined}
                    tabIndex={index === slicedIndex - 9 ? -1 : undefined}
                  />
                ))}
          </StyledJobsDiv>
          {slicedIndex < jobs.length && (
            <StyledButtonDiv>
              <StyeldButton onClick={loadMoreJobs}>משרות נוספות</StyeldButton>
            </StyledButtonDiv>
          )}
        </>
      ) : (
        <StyledLoaderDiv>
          {animationData && (
            <Lottie animationData={animationData} autoplay={true} />
          )}
          <Typography variant="body1" style={{ paddingBottom: "16px" }}>
            מכינים עבורך את המשרות
          </Typography>
        </StyledLoaderDiv>
      )}
    </Container>
  )
}

export default JobDisplay
